"use client"
import type { EstimateContent } from "../booking/small-content"
import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import Link from "next/link"
import { Footer } from "@/components/layout/Footer"
import { MathCaptcha } from "@/components/ui/MathCaptcha"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import { HeroRings, DotGrid, CornerAccents } from "@/components/ui/BackgroundDecor"
import { CheckCircle, AlertCircle, Send } from "lucide-react"
import { genericContent, type Currency } from "../offers/content"
import { computeQuote, specialAvailable, getPackage, PACKAGE_KEYS, type PackageKey, type Billing } from "@/lib/estimate"
import { GuideChat } from "./GuideChat"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
// 4.5:1-safe orange for small text on the dark surfaces (the brand accent is 3.8–4.2:1 there).
const ACCENT_TEXT = "#E06B2E"
const MUTED = "rgba(255,255,255,0.6)"
const SURFACE = "rgba(255,255,255,0.05)"
const BORDER = "rgba(255,255,255,0.08)"

const CURRENCIES: Record<Currency, { symbol: string; label: string; flag: string; region: string }> = {
  GBP: { symbol: "£", label: "GBP", flag: "🇬🇧", region: "United Kingdom" },
  ZAR: { symbol: "R", label: "ZAR", flag: "🇿🇦", region: "South Africa" },
}

function fmtPrice(currency: Currency, amount: number) {
  return `${CURRENCIES[currency].symbol}${amount.toLocaleString("en-US")}`
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${BORDER}`,
  borderRadius: 12,
  padding: "14px 16px",
  color: "#fff",
  fontSize: 15,
  fontFamily: "inherit",
}

type Status = "idle" | "loading" | "success" | "error"

export function EstimateClient({ initialCurrency, copy }: { initialCurrency: Currency; copy: EstimateContent }) {
  const [currency, setCurrency] = useState<Currency>(initialCurrency)
  const [pkg, setPkg] = useState<PackageKey>("chatbot")
  const [billing, setBilling] = useState<Billing>("monthly")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [consent, setConsent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>("idle")
  const [estimateSent, setEstimateSent] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  // Bumped to remount the captcha (tokens are single-use) when quoting again.
  const [captchaKey, setCaptchaKey] = useState(0)

  function quoteAnother() {
    setStatus("idle")
    setCaptchaToken(null)
    setCaptchaKey((k) => k + 1)
  }

  // Honour a saved manual choice over the geo default (same key as /offers).
  useEffect(() => {
    try {
      const saved = localStorage.getItem("bp_currency")
      if (saved && saved in CURRENCIES) setCurrency(saved as Currency)
    } catch { /* localStorage blocked — geo default stands */ }
  }, [])

  function chooseCurrency(c: Currency) {
    setCurrency(c)
    try { localStorage.setItem("bp_currency", c) } catch { /* ignore */ }
  }

  // A launch special is only valid for the package + currency it was declared on
  // — fall back if the selection changes under it. None is running right now
  // (the ZAR Opening Special was retired 2026-08-24); this reads straight from
  // offers/content.ts so a future one lights the option up on its own, and no
  // stale offer copy or price ships in the bundle meanwhile.
  const specialOk = specialAvailable(pkg, currency)
  const special = specialOk ? getPackage(pkg).openingSpecial : undefined
  const activeBilling: Billing = billing === "special" && !specialOk ? "monthly" : billing

  const quote = useMemo(() => computeQuote(pkg, activeBilling, currency), [pkg, activeBilling, currency])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setFieldError(null)
    if (!firstName.trim()) return setFieldError("Please enter your name.")
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setFieldError("Please enter a valid email — that's where your estimate goes.")
    if (!consent) return setFieldError("Please agree to the privacy policy so we can send your estimate.")
    if (!captchaToken) return setFieldError("Please complete the verification below.")

    setStatus("loading")
    try {
      const res = await fetch("/api/estimate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageKey: pkg,
          billing: activeBilling,
          currency,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          companyName: companyName.trim(),
          consent: true,
          recaptchaToken: captchaToken,
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error("failed")
      setEstimateSent(!!data.estimateSent)
      setStatus("success")
    } catch {
      setStatus("error")
    }
  }

  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      {/* HERO */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.08, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 80% 65% at 85% 25%, rgba(196,84,26,0.40) 0%, transparent 66%)` }}
        />
        <HeroRings side="right" />
        <DotGrid />
        <CornerAccents />
        <section style={{ padding: "clamp(100px, 16vw, 150px) clamp(20px, 4vw, 48px) 36px", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <ScrollReveal effect="fade-up">
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 28 }}>
              <span style={{ height: 1, width: 32, background: ACCENT, display: "block" }} />
              <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{copy.eyebrow}</span>
            </div>
            <h1 style={{ fontSize: "clamp(40px, 7vw, 96px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.9, marginBottom: 28 }}>
              {copy.headingLine1}<br /><span style={{ color: ACCENT }}>{copy.headingLine2}</span>
            </h1>
            <p style={{ fontSize: "clamp(15px, 1.4vw, 19px)", color: MUTED, maxWidth: 560, lineHeight: 1.65 }}>
              {copy.introBefore}{" "}
              <Link href="/offers" style={{ color: ACCENT, fontWeight: 600 }}>{copy.introLinkLabel}</Link>{copy.introAfter}
            </p>
          </ScrollReveal>
        </section>
      </div>

      <AnimatePresence mode="wait">
        {status === "success" ? (
          /* ── SUCCESS STATE ── */
          <motion.section
            key="success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ padding: "20px clamp(20px, 4vw, 48px) 100px", maxWidth: 720, margin: "0 auto", textAlign: "center" }}
          >
            <div style={{ padding: "clamp(36px, 5vw, 56px)", borderRadius: 24, border: `1px solid rgba(196,84,26,0.45)`, background: `linear-gradient(160deg, rgba(196,84,26,0.12) 0%, ${SURFACE} 55%)` }}>
              <CheckCircle size={44} style={{ color: ACCENT, marginBottom: 20 }} />
              <h2 style={{ fontSize: "clamp(26px, 3vw, 38px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 14 }}>
                {estimateSent ? "Your estimate is on its way." : "We've got your details."}
              </h2>
              <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.7, maxWidth: 460, margin: "0 auto 28px" }}>
                {estimateSent
                  ? `Check ${email} — your ${quote.packageName} estimate should arrive within a minute or two. Accept it right from the email when you're ready.`
                  : `Your ${quote.packageName} estimate is being prepared and will arrive at ${email} shortly.`}
              </p>
              <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
                <Link href="/booking" style={{ display: "inline-block", padding: "15px 36px", background: ACCENT, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 15, textDecoration: "none" }}>
                  Want to talk it through? Book a call →
                </Link>
                <button onClick={quoteAnother}
                  style={{ padding: "15px 36px", background: "transparent", border: `1px solid ${BORDER}`, color: "#fff", borderRadius: 100, fontWeight: 600, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
                  Get another estimate
                </button>
              </div>
            </div>
          </motion.section>
        ) : (
          <motion.div key="form" exit={{ opacity: 0 }}>
            {/* AI GUIDE — remounts on currency switch so quoted prices stay consistent */}
            <section style={{ padding: "0 clamp(20px, 4vw, 48px) 36px", maxWidth: 1100, margin: "0 auto" }}>
              <GuideChat
                key={currency}
                currency={currency}
                pkg={pkg}
                billing={activeBilling}
                onRecommend={(p, b) => {
                  if (p) setPkg(p)
                  if (b) setBilling(b)
                }}
              />
            </section>

            {/* CURRENCY TOGGLE */}
            <section style={{ padding: "0 clamp(20px, 4vw, 48px)", maxWidth: 1100, margin: "0 auto" }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, justifyContent: "space-between", marginBottom: 28 }}>
                <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ background: ACCENT, color: "#fff", borderRadius: 100, fontSize: 11, fontWeight: 800, padding: "2px 10px" }}>1</span>
                    Prices in <span style={{ color: "#fff", fontWeight: 600 }}>{CURRENCIES[currency].label}</span> for {CURRENCIES[currency].region} — switch anytime.
                  </span>
                </p>
                <div role="group" aria-label="Choose currency" style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 100, border: `1px solid ${BORDER}`, background: SURFACE }}>
                  {(Object.keys(CURRENCIES) as Currency[]).map((c) => {
                    const active = c === currency
                    return (
                      <button key={c} onClick={() => chooseCurrency(c)} aria-pressed={active}
                        style={{ position: "relative", padding: "8px 18px", borderRadius: 100, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "none", color: active ? "#fff" : MUTED }}>
                        {active && (
                          <motion.span layoutId="estCurrencyPill" style={{ position: "absolute", inset: 0, borderRadius: 100, background: ACCENT, zIndex: 0 }} transition={{ type: "spring", stiffness: 380, damping: 30 }} />
                        )}
                        <span style={{ position: "relative", zIndex: 1 }}>{CURRENCIES[c].flag} {CURRENCIES[c].label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </section>

            {/* STEP 2 — PACKAGE */}
            <section style={{ padding: "0 clamp(20px, 4vw, 48px) 8px", maxWidth: 1100, margin: "0 auto" }}>
              {/* h2, not p: these step labels are the form's section headings. Without them the
                  outline ran h1 → h3 (package names), which Lighthouse flags as a skipped level. */}
              <h2 style={{ fontSize: 13, fontWeight: 400, color: MUTED, margin: "0 0 16px", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: ACCENT, color: "#fff", borderRadius: 100, fontSize: 11, fontWeight: 800, padding: "2px 10px" }}>2</span>
                Pick your package
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 16 }}>
                {PACKAGE_KEYS.map((key) => {
                  const p = getPackage(key)
                  const active = key === pkg
                  return (
                    <motion.button
                      key={key}
                      onClick={() => setPkg(key)}
                      aria-pressed={active}
                      whileHover={{ y: -4 }}
                      transition={{ type: "spring", stiffness: 320, damping: 26 }}
                      style={{
                        textAlign: "left",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        color: "#fff",
                        padding: "clamp(20px, 2.4vw, 28px)",
                        borderRadius: 20,
                        border: active ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
                        background: active ? `linear-gradient(160deg, rgba(196,84,26,0.14) 0%, ${SURFACE} 55%)` : SURFACE,
                        boxShadow: active ? "0 0 40px rgba(196,84,26,0.16)" : "none",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <span style={{ fontSize: 11, letterSpacing: "0.15em", color: MUTED }}>{p.number}</span>
                        {p.badge && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: active ? ACCENT_TEXT : MUTED }}>{p.badge}</span>}
                      </div>
                      <h3 style={{ fontSize: "clamp(17px, 1.6vw, 21px)", fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6 }}>{p.name}</h3>
                      <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.5, marginBottom: 12 }}>{p.forWho}</p>
                      <p style={{ fontSize: 20, fontWeight: 900 }}>
                        {fmtPrice(currency, p.price[currency])}<span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>/mo</span>
                      </p>
                      <span aria-hidden style={{ display: "inline-block", marginTop: 12, fontSize: 12, fontWeight: 700, color: active ? ACCENT_TEXT : MUTED }}>
                        {active ? "✓ Selected" : "Select"}
                      </span>
                    </motion.button>
                  )
                })}
                {/* 03 — Custom Systems: no instant price, routes to a scoping call */}
                <Link
                  href="/contact?variant=estimate-custom"
                  style={{ display: "block", textDecoration: "none", color: "#fff", padding: "clamp(20px, 2.4vw, 28px)", borderRadius: 20, border: `1px dashed ${BORDER}`, background: SURFACE }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ fontSize: 11, letterSpacing: "0.15em", color: MUTED }}>{genericContent.customPackage.number}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED }}>{genericContent.customPackage.badge}</span>
                  </div>
                  <h3 style={{ fontSize: "clamp(17px, 1.6vw, 21px)", fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 6 }}>{genericContent.customPackage.name}</h3>
                  <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.5, marginBottom: 12 }}>{genericContent.customPackage.forWho}</p>
                  <p style={{ fontSize: 20, fontWeight: 900 }}>
                    {genericContent.customPackage.floor[currency] ? `From ${fmtPrice(currency, genericContent.customPackage.floor[currency]!)}` : "Quoted per project"}
                  </p>
                  <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.5, marginTop: 8 }}>No instant price for this one. Book a scoping call and we&rsquo;ll give you a fixed quote before any work starts.</p>
                  <span style={{ display: "inline-block", marginTop: 12, fontSize: 12, fontWeight: 700, color: ACCENT_TEXT }}>Book a scoping call →</span>
                </Link>
              </div>
            </section>

            {/* STEP 3 — BILLING */}
            <section style={{ padding: "28px clamp(20px, 4vw, 48px) 8px", maxWidth: 1100, margin: "0 auto" }}>
              <h2 style={{ fontSize: 13, fontWeight: 400, color: MUTED, margin: "0 0 16px", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: ACCENT, color: "#fff", borderRadius: 100, fontSize: 11, fontWeight: 800, padding: "2px 10px" }}>3</span>
                How would you like to pay?
              </h2>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {([
                  { key: "monthly" as Billing, label: "Monthly", sub: "+ one-time setup fee" },
                  { key: "annual" as Billing, label: "Annual", sub: "2 months free · half setup" },
                  ...(special ? [{ key: "special" as Billing, label: `★ ${special.label}`, sub: `${fmtPrice(currency, special.upfront)} once-off + ${fmtPrice(currency, special.monthly)}/mo` }] : []),
                ]).map((b) => {
                  const active = b.key === activeBilling
                  return (
                    <button key={b.key} onClick={() => setBilling(b.key)} aria-pressed={active}
                      style={{
                        fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                        padding: "14px 22px", borderRadius: 16,
                        border: active ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
                        background: active ? "rgba(196,84,26,0.12)" : SURFACE, color: "#fff",
                      }}>
                      <span style={{ display: "block", fontSize: 14, fontWeight: 800 }}>{b.label}</span>
                      <span style={{ display: "block", fontSize: 12, color: active ? ACCENT : MUTED, fontWeight: 600, marginTop: 2 }}>{b.sub}</span>
                    </button>
                  )
                })}
              </div>
            </section>

            {/* QUOTE BREAKDOWN + FORM */}
            <section style={{ padding: "32px clamp(20px, 4vw, 48px) 100px", maxWidth: 1100, margin: "0 auto" }}>
              <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 20, alignItems: "start" }}>
                {/* Breakdown */}
                <div style={{ padding: "clamp(24px, 3vw, 36px)", borderRadius: 24, border: `1px solid rgba(196,84,26,0.45)`, background: `linear-gradient(160deg, rgba(196,84,26,0.10) 0%, ${SURFACE} 55%)` }}>
                  <p style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: MUTED, marginBottom: 18 }}>Your estimate</p>
                  <h3 style={{ fontSize: "clamp(20px, 2vw, 26px)", fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 20 }}>{quote.packageName}</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                    {quote.lines.map((l) => (
                      <div key={l.label} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14, lineHeight: 1.5 }}>
                        <span>
                          <span style={{ fontWeight: 600 }}>{l.label}</span>
                          <span style={{ display: "block", fontSize: 12, color: MUTED }}>{l.qty > 1 ? `${l.qty} × ${fmtPrice(currency, l.unitAmount)} · ` : ""}{l.detail}</span>
                        </span>
                        <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{fmtPrice(currency, l.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                      <span style={{ color: MUTED }}>Total, first {quote.termMonths} months</span>
                      <span style={{ fontWeight: 800 }}>{fmtPrice(currency, quote.total)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 14, color: MUTED }}>Due today</span>
                      <span style={{ fontSize: 26, fontWeight: 900, color: ACCENT }}>{fmtPrice(currency, quote.dueToday)}</span>
                    </div>
                    {quote.monthlyOngoing > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                        <span style={{ color: MUTED }}>{quote.monthlyStartsAtLaunch ? "From launch day" : "Then"}</span>
                        <span style={{ fontWeight: 700 }}>{fmtPrice(currency, quote.monthlyOngoing)}/mo</span>
                      </div>
                    )}
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                    {quote.notes.map((n) => (
                      <li key={n} style={{ fontSize: 12, color: MUTED, lineHeight: 1.5, display: "flex", gap: 8 }}>
                        <span style={{ color: ACCENT, flexShrink: 0 }}>✦</span>{n}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contact form */}
                <form onSubmit={submit} style={{ padding: "clamp(24px, 3vw, 36px)", borderRadius: 24, border: `1px solid ${BORDER}`, background: SURFACE, display: "flex", flexDirection: "column", gap: 14 }}>
                  <h2 style={{ fontSize: 13, fontWeight: 400, color: MUTED, display: "inline-flex", alignItems: "center", gap: 8, margin: "0 0 4px" }}>
                    <span style={{ background: ACCENT, color: "#fff", borderRadius: 100, fontSize: 11, fontWeight: 800, padding: "2px 10px" }}>4</span>
                    Where should we send it?
                  </h2>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name *" aria-label="First name" style={inputStyle} />
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" aria-label="Last name" style={inputStyle} />
                  </div>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email — your estimate goes here *" aria-label="Email address" style={inputStyle} />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="Phone (optional)" aria-label="Phone number (optional)" style={inputStyle} />
                  <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company (optional)" aria-label="Company name (optional)" style={inputStyle} />

                  <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      style={{ marginTop: 3, flexShrink: 0, accentColor: ACCENT, width: 16, height: 16, cursor: "pointer" }} />
                    <span style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.6 }}>
                      I agree to BoldPiq&apos;s{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: ACCENT_TEXT, textDecoration: "underline" }}>Privacy Policy</a>
                      {" "}and consent to my information being processed to prepare and send my estimate, including by automated systems and AI-assisted tools. I understand I may be contacted by email, SMS, WhatsApp or phone.
                    </span>
                  </label>

                  <MathCaptcha key={captchaKey} onVerified={(ok, token) => setCaptchaToken(ok ? token : null)} />

                  {fieldError && (
                    <p role="alert" style={{ display: "flex", alignItems: "center", gap: 6, color: "#f43f5e", fontSize: 13 }}>
                      <AlertCircle size={14} /> {fieldError}
                    </p>
                  )}
                  {status === "error" && (
                    <p role="alert" style={{ display: "flex", alignItems: "center", gap: 6, color: "#f43f5e", fontSize: 13 }}>
                      <AlertCircle size={14} /> Something went wrong sending your estimate. Please try again — or <Link href="/contact" style={{ color: ACCENT }}>contact us</Link> and we&rsquo;ll quote you directly.
                    </p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={status === "loading"}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      padding: "16px 32px", borderRadius: 100, background: ACCENT, color: "#fff",
                      fontWeight: 700, fontSize: 15, border: "none", fontFamily: "inherit",
                      cursor: status === "loading" ? "wait" : "pointer",
                    }}
                  >
                    {status === "loading"
                      ? "Preparing your estimate…"
                      : <><Send size={15} /> Email me my estimate — {fmtPrice(currency, quote.dueToday)} to start</>}
                  </motion.button>
                  <p style={{ fontSize: 11.5, color: MUTED, textAlign: "center", lineHeight: 1.5 }}>
                    No payment now — this sends a formal estimate you can accept or decline. If you go ahead and don&rsquo;t love your homepage design, you get every cent back.
                  </p>
                </form>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </main>
  )
}
